/**
 * useAppInit - Application initialization hook
 *
 * Handles:
 * - Clearing old localStorage data (migration to GraphQL-only)
 * - Loading data from GraphQL backend
 * - Managing initialization state
 */

import { useEffect, useState } from 'react';
import { useProjectsStore } from '@business-assistant/shared';
import { useChatStore } from '@business-assistant/shared/services';
import { useSettingsStore } from '@business-assistant/module-settings';
import i18n from '../i18n';

// Version for localStorage migration - increment to force clear
const STORAGE_VERSION = 'graphql-v1';

/**
 * Clear old localStorage data from mock/seed era
 * Only runs once per version
 */
function clearOldStorageData(): void {
  const versionKey = 'ba-storage-version';
  const currentVersion = localStorage.getItem(versionKey);

  if (currentVersion === STORAGE_VERSION) {
    return; // Already migrated
  }

  console.log('[useAppInit] Clearing old localStorage data for GraphQL migration...');

  // List of old storage keys to remove
  const keysToRemove = [
    'projects-store',
    'accounting-store',
    'hub-store',
    'calendar-storage',
    'settings-store',
    'links-store',
    'timer-store',
    'chat-store',
  ];

  keysToRemove.forEach(key => {
    if (localStorage.getItem(key)) {
      console.log(`[useAppInit] Removing: ${key}`);
      localStorage.removeItem(key);
    }
  });

  // Mark as migrated
  localStorage.setItem(versionKey, STORAGE_VERSION);
  console.log('[useAppInit] localStorage cleanup complete');
}

interface AppInitState {
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

export function useAppInit() {
  const [state, setState] = useState<AppInitState>({
    loading: true,
    error: null,
    initialized: false,
  });

  useEffect(() => {
    async function initializeApp() {
      console.log('[useAppInit] Starting initialization...');
      try {
        setState({ loading: true, error: null, initialized: false });

        // Step 0: Clear old localStorage data from mock/seed era
        clearOldStorageData();

        // Step 1: Load projects from GraphQL backend
        console.log('[useAppInit] Loading projects from GraphQL...');
        await useProjectsStore.getState().loadFromServer();

        // Step 2: Load data from GraphQL for all stores
        // Use getState() to access store methods directly (avoids stale closure issues)
        console.log('[useAppInit] Loading other stores from GraphQL...');

        const chatLoadData = useChatStore.getState().loadData;
        console.log('[useAppInit] chatStore.loadData exists:', !!chatLoadData);

        await Promise.all([
          // Hub, Calendar, Accounting, Links, Timer data are now fetched
          // automatically by react-query hooks when components mount.
          chatLoadData?.() ?? Promise.resolve(),
          useSettingsStore.getState().loadTeamMembers?.() ?? Promise.resolve(),
          useSettingsStore.getState().loadProfile?.() ?? Promise.resolve(),
        ]);

        const chatSessions = useChatStore.getState().sessions;
        console.log('[useAppInit] Chat sessions after load:', chatSessions.length);

        // Language preference is managed via localStorage only (i18next handles this)
        // Backend doesn't have user profile locale support yet
        console.log('[useAppInit] Current language:', i18n.language);

        console.log('[useAppInit] Initialization complete!');
        setState({ loading: false, error: null, initialized: true });
      } catch (error) {
        console.error('[useAppInit] Initialization error:', error);
        setState({
          loading: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          initialized: false,
        });
      }
    }

    initializeApp();
  }, []); // Run only once on mount

  return state;
}
